import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilter, FaSearch, FaSync, FaCalendar, FaTimes } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';

function FactureFiltre({ filters, onFilterChange, loading, totalCount }) {
  const { t } = useTranslation('facturation');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const statutOptions = [
    { value: '', label: t('filters.status.all') },
    { value: '0', label: `📝 ${t('filters.status.draft')}` },
    { value: '1', label: `✅ ${t('filters.status.validated')}` }
  ];

  const trimestreOptions = [
    { value: '', label: t('filters.quarter.all') },
    { value: '1', label: t('filters.quarter.q1') },
    { value: '2', label: t('filters.quarter.q2') },
    { value: '3', label: t('filters.quarter.q3') },
    { value: '4', label: t('filters.quarter.q4') }
  ];

  const anneeOptions = [
    { value: '', label: t('filters.year.all') },
    { value: '2023', label: t('filters.year.2023') },
    { value: '2024', label: t('filters.year.2024') },
    { value: '2025', label: t('filters.year.2025') }
  ];

  const sortOptions = [
    { value: 'creationDate', label: t('filters.sort.creationDate') },
    { value: 'date', label: t('filters.sort.invoiceDate') },
    { value: 'accountName', label: t('filters.sort.accountName') },
    { value: 'total', label: t('filters.sort.total') }
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({ [key]: value, pageNumber: 1 });
  };

  const handleSearchChange = (e) => {
    handleFilterChange('accountName', e.target.value);
  };

  const handleClearFilters = () => {
    onFilterChange({
      accountName: '',
      statut: '',
      trimestre: '',
      annee: '',
      dateDebut: '',
      dateFin: '',
      sortBy: '',
      sortDescending: false,
      pageNumber: 1
    });
  };

  const hasActiveFilters = filters.accountName || filters.statut || filters.trimestre || 
                          filters.annee || filters.dateDebut || filters.dateFin;

  const getActiveFilterText = () => {
    const parts = [];
    if (filters.accountName) parts.push(t('filters.activeFilters.client', { value: filters.accountName }));
    if (filters.statut) parts.push(t('filters.activeFilters.status', { 
      value: statutOptions.find(o => o.value === filters.statut)?.label 
    }));
    if (filters.trimestre) parts.push(t('filters.activeFilters.quarter', { 
      value: trimestreOptions.find(o => o.value === filters.trimestre)?.label 
    }));
    if (filters.annee) parts.push(t('filters.activeFilters.year', { value: filters.annee }));
    if (filters.dateDebut) parts.push(t('filters.activeFilters.startDate', { value: filters.dateDebut }));
    if (filters.dateFin) parts.push(t('filters.activeFilters.endDate', { value: filters.dateFin }));
    
    return parts.join(' • ');
  };

  return (
    <div className="search-filter-container" style={{ marginTop: '-30px'}}>
      <h1 className="card-title fw-bold text-primary-emphasis  text-uppercase fs-2" style={{ letterSpacing: "0.10em" }}>
        {t('page.title')} 
        {totalCount > 0 && <span className="badge bg-primary ms-2 mb-1">{totalCount}</span>}
      </h1>
      
      <div className="filter-controls mb-3">
        {/* Recherche */}
        <div className="filter-item filter-search">
          <label className="filter-label">
            <svg className="label-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            Recherche
          </label>
          <div className="search-input-wrapper">
            <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input 
              type="text" 
              className="filter-input search-input" 
              placeholder={t('filters.search.placeholder')} 
              value={filters.accountName || ''}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {/* Filtre Statut */}
        <div className="filter-item">
          <label className="filter-label">
            <svg className="label-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Statut
          </label>
          <div className="select-wrapper">
            <select 
              className="filter-select" 
              value={filters.statut || ''}
              onChange={(e) => handleFilterChange('statut', e.target.value)}
            >
              {statutOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <svg className="select-arrow" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </div>
        </div>

        {/* Filtre Trimestre */}
        <div className="filter-item">
          <label className="filter-label">
            <svg className="label-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
            </svg>
            Trimestre
          </label>
          <div className="select-wrapper">
            <select
              className="filter-select"
              value={filters.EntityName || ''}
              onChange={(e) => handleFilterChange('trimestre', e.target.value)}
            >
              {trimestreOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <svg className="select-arrow" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 10l5 5 5-5z"/>
            </svg>
          </div>
        </div>

        {/* Bouton Filtres Avancés */}
        <div className="filter-item">
          <label className="filter-label">
            <svg className="label-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Filtre avance
          </label>
          <button
            className={`filter-select ${showAdvanced ? 'filter-button' : 'filter-button'}`}
            onClick={() => setShowAdvanced(!showAdvanced)}
            data-tooltip-id="advanced-filters"
            data-tooltip-content={t('page.actions.advancedFilters')}
          >
            <FaFilter className="me-1" />
            {t('page.actions.advancedFilters')}
            {hasActiveFilters && <span className="badge bg-danger ms-1">cliquez pour voir les filtres actifs</span>}
          </button>
        </div>

        {/* Bouton Actualiser */}
        <div className="filter-item">
          <label className="filter-label">
            <svg className="label-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Actualiser
          </label>
          <button 
            className="filter-select"
            onClick={() => onFilterChange({ ...filters })}
            disabled={loading}
            data-tooltip-id="refresh-button"
            data-tooltip-content={t('page.actions.refresh')}
          >
            <FaSync className={loading ? 'spinner' : ''} />
          </button>
        </div>

        {/* Effacer les filtres */}
        <div className="position-relative">
          {hasActiveFilters && (
            <button
              className="refresh-button"
              onClick={handleClearFilters}
              data-tooltip-id="clear-filters"
              data-tooltip-content={t('page.actions.clearFilters')}
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* Filtres Avancés */}
      {showAdvanced && (
        <div className="advanced-filters card border-0 shadow-sm">
          <div className="card-body">
            <h6 className="card-title mb-3">
              <FaFilter className="me-2 text-primary" />
              {t('filters.title')}
            </h6>
            
            <div className="row g-3">
              {/* Année */}
              <div className="col-md-3">
                <label className="form-label-sm fw-semibold">{t('filters.year.label')}</label>
                <select 
                  className="account-type-select"
                  value={filters.annee || ''}
                  onChange={(e) => handleFilterChange('annee', e.target.value)}
                >
                  {anneeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Début */}
              <div className="col-md-3">
                <label className="form-label-sm fw-semibold">{t('filters.date.start')}</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <FaCalendar size={12} />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateDebut || ''}
                    onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                  />
                </div>
              </div>

              {/* Date Fin */}
              <div className="col-md-3">
                <label className="form-label-sm fw-semibold">{t('filters.date.end')}</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">
                    <FaCalendar size={12} />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateFin || ''}
                    onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                  />
                </div>
              </div>

              {/* Tri */}
              <div className="col-md-3">
                <label className="form-label-sm fw-semibold">{t('filters.sort.label')}</label>
                <div className="d-flex gap-2">
                  <select 
                    className="account-type-select"
                    value={filters.sortBy || ''}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  >
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className={`filter-button ${filters.sortDescending ? 'filter-button' : 'filter-button'}`}
                    onClick={() => handleFilterChange('sortDescending', !filters.sortDescending)}
                    data-tooltip-id="sort-direction"
                    data-tooltip-content={filters.sortDescending ? 
                      t('filters.sort.descending') : 
                      t('filters.sort.ascending')}
                  >
                    {filters.sortDescending ? '↓' : '↑'}
                  </button>
                </div>
              </div>
            </div>

            {/* Indicateur de filtres actifs */}
            {hasActiveFilters && (
              <div className="mt-3 p-2 bg-light rounded">
                <small className="text-muted">
                  <strong>{t('filters.active')}:</strong> {getActiveFilterText()}
                </small>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tooltips */}
      <Tooltip id="advanced-filters" place="top" />
      <Tooltip id="refresh-button" place="top" />
      <Tooltip id="clear-filters" place="top" />
      <Tooltip id="sort-direction" place="top" />
    </div>
  );
}

export default FactureFiltre;